import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Sparkles, Zap, Shield, Brain, LineChart, GitBranch,
  Lock, Cpu, FileText, ArrowUpRight,
} from 'lucide-react';
import { BRAND, AGENTS } from '../lib/brand';
import { Reveal, Stagger, StaggerItem, Eyebrow, Display } from '../components/instrument';
import { LiveNumber } from '../components/instrument/LiveNumber';
import { CognaraLogo } from '../components/instrument/CognaraLogo';
import { CognaraWordmark } from '../components/instrument/CognaraWordmark';
import { fadeUp, staggerContainer, staggerChild } from '../lib/motionConfig';

/**
 * Cognara — Marketing Landing
 * Living Control Room hero + Bento feature grid.
 * Preserves Steel & Ember palette and instrument-grade aesthetic.
 */

const Landing = () => {
  const gradientRef = useRef(null);

  // Cursor-reactive hero gradient
  useEffect(() => {
    const el = gradientRef.current;
    if (!el) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--mx', `${x}%`);
      el.style.setProperty('--my', `${y}%`);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="dark min-h-screen" style={{ background: 'var(--layer-0)' }}>
      {/* NAV */}
      <nav className="fixed top-0 inset-x-0 z-40 h-[64px] flex items-center px-6 border-b"
           style={{ background: 'rgba(10,14,26,0.7)', backdropFilter: 'blur(12px)', borderColor: 'var(--hairline)' }}>
        <div className="max-w-[1280px] mx-auto w-full flex items-center">
          <Link to="/" className="flex items-center gap-2.5" aria-label="Cognara home">
            <CognaraLogo variant="icon" size={30} style={{ color: 'var(--nexus-text-primary)' }} />
            <CognaraWordmark size={20} color="var(--nexus-text-primary)" />
          </Link>
          <div className="flex-1 flex items-center justify-center gap-8 text-[13px] text-nexus-text-secondary">
            <a href="#agents" className="hover:text-nexus-text-primary transition-colors">Agents</a>
            <a href="#telemetry" className="hover:text-nexus-text-primary transition-colors">Telemetry</a>
            <a href="#bento" className="hover:text-nexus-text-primary transition-colors">Platform</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/portal/login" className="text-[13px] text-nexus-text-secondary hover:text-nexus-text-primary transition-colors">
              Sign in
            </Link>
            <Link
              to="/portal/login"
              className="ig-cta flex items-center gap-1.5 h-9 px-4 rounded-lg text-[13px] font-medium"
              style={{ background: '#1E4E8C', color: '#E6EEF9' }}
            >
              Launch console <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section
        ref={gradientRef}
        className="relative pt-[64px] min-h-[820px] flex items-center overflow-hidden"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at var(--mx,70%) var(--my,30%), rgba(242,107,31,0.08), transparent 60%),
            radial-gradient(ellipse 50% 40% at 20% 80%, rgba(30,78,140,0.10), transparent 60%),
            linear-gradient(180deg,#0A0E1A 0%,#0F141F 100%)
          `,
        }}
      >
        {/* Grid underlay */}
        <div className="absolute inset-0 pointer-events-none opacity-40 ig-grid-sweep"
             style={{
               backgroundImage:
                 'linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)',
               backgroundSize: '64px 64px',
               maskImage: 'radial-gradient(ellipse at center,black 30%,transparent 85%)',
             }} />

        <div className="relative max-w-[1280px] mx-auto w-full grid grid-cols-12 gap-8 px-6 py-24">
          {/* Copy */}
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer(0.06)}
            className="col-span-12 lg:col-span-7 pt-8"
          >
            <motion.div variants={staggerChild} className="flex items-center gap-3 mb-6">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: 24 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="h-px bg-nexus-accent-blue"
              />
              <Eyebrow>Instrument-Grade · Multi-Agent · Enterprise</Eyebrow>
            </motion.div>

            <motion.h1 variants={staggerChild} className="ig-display text-[clamp(42px,6vw,72px)] leading-[1.02] tracking-[-0.025em] text-nexus-text-primary mb-5">
              The expert engine
              <br />
              for every capital
              <br />
              <span style={{ color: '#F26B1F' }}>decision</span>.
            </motion.h1>

            <motion.p variants={staggerChild} className="text-[18px] leading-[1.6] text-nexus-text-secondary max-w-[560px] mb-8">
              {BRAND.name} is a multi-agent control plane for capital projects. Route questions to <strong className="text-nexus-text-primary font-medium">{AGENTS.sutradhar.display}</strong>,
              compress long context with audit-grade memory, and keep institutional knowledge live across every thread.
            </motion.p>

            <motion.div variants={staggerChild} className="flex flex-wrap items-center gap-3 mb-10">
              <Link
                to="/portal/login"
                className="ig-cta flex items-center gap-2 h-12 px-6 rounded-xl text-[14px] font-medium"
                style={{ background: '#1E4E8C', color: '#E6EEF9', boxShadow: 'var(--tinted-shadow-cta)' }}
              >
                Launch the console <ArrowRight size={15} />
              </Link>
              <a href="#bento"
                 className="flex items-center gap-2 h-12 px-6 rounded-xl text-[14px] font-medium text-nexus-text-primary transition-colors"
                 style={{ border: '1px solid var(--hairline-strong)', background: 'var(--layer-1)' }}>
                See how it works
              </a>
            </motion.div>
          </motion.div>

          {/* Live product vignette */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="col-span-12 lg:col-span-5"
          >
            <HeroVignette />
          </motion.div>
        </div>
      </section>

      {/* METRICS STRIP */}
      <section className="relative border-y" style={{ borderColor: 'var(--hairline)', background: 'var(--layer-1)' }}>
        <div className="max-w-[1280px] mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { label: 'Avg context retained', value: 98, suffix: '%' },
            { label: 'Conversation cost cut', value: 62, suffix: '%' },
            { label: 'Orchestrator uptime', value: 99.97, suffix: '%', decimals: 2 },
            { label: 'Pinned facts enforced', value: 100, suffix: '%' },
          ].map((m, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <Eyebrow>{m.label}</Eyebrow>
              <div className="ig-display text-[40px] text-nexus-text-primary mt-1" style={{ color: '#E4E7ED' }}>
                <LiveNumber value={m.value} suffix={m.suffix} decimals={m.decimals || 0} />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BENTO */}
      <section id="bento" className="relative py-28">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="max-w-[680px] mb-12">
            <Eyebrow>Platform · capabilities</Eyebrow>
            <Display as="h2" size="lg" className="mt-3 mb-4 text-nexus-text-primary">
              A control plane, not a chatbot.
            </Display>
            <p className="text-[17px] leading-[1.6] text-nexus-text-secondary">
              Every surface is built for operators in billion-dollar programs — telemetry-dense, audit-ready, keyboard-first.
            </p>
          </div>

          <Stagger stagger={0.05} className="grid grid-cols-12 gap-5">
            <BentoTile col="col-span-12 lg:col-span-8" icon={Brain} eyebrow="Agent · orchestrator" title="Cognara remembers what matters" body="Per-conversation auto-summarization + Pin-a-Fact surface, so 200-turn threads stay precise and cheap." demo={<MemoryDemo />} />
            <BentoTile col="col-span-12 lg:col-span-4" icon={Shield} eyebrow="Compliance" title="Audit-ready by default" body="Every answer carries a source trail. Export conversations to PDF/JSON on demand." />
            <BentoTile col="col-span-12 lg:col-span-4" icon={LineChart} eyebrow="Telemetry" title="Live cost controls" body="Usage-based alerts, per-tenant metering, quota-aware streaming." demo={<TelemetryDemo />} />
            <BentoTile col="col-span-12 lg:col-span-4" icon={Zap} eyebrow="Throughput" title="SSE from first token" body="Tool-use cards stream progressively. No spinners, no dead air." />
            <BentoTile col="col-span-12 lg:col-span-4" icon={Lock} eyebrow="Multi-tenant" title="Hard isolation, soft branding" body="Tenant-scoped KBs, custom domains, white-label CSS tokens." />
            <BentoTile col="col-span-12 lg:col-span-6" icon={GitBranch} eyebrow="Knowledge" title="Foundation + tenant dual-RAG" body="Our curated corpus underpins every answer; your documents refine it." />
            <BentoTile col="col-span-12 lg:col-span-6" icon={Cpu} eyebrow="Models" title="Cognara Engine · Ultra 4.7" body="Redundant inference routing keeps you live during provider outages." />
          </Stagger>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 border-t overflow-hidden" style={{ borderColor: 'var(--hairline)' }}>
        <div className="absolute inset-0 opacity-60 pointer-events-none"
             style={{ background: 'radial-gradient(ellipse at center, rgba(30,78,140,0.12), transparent 70%)' }} />
        <div className="relative max-w-[720px] mx-auto px-6 text-center">
          <Eyebrow>Start in 60 seconds</Eyebrow>
          <Display as="h2" size="lg" className="mt-3 mb-5 text-nexus-text-primary">
            Your expert engine, <span className="ig-editorial" style={{ color: '#F26B1F' }}>on call</span>.
          </Display>
          <p className="text-[17px] leading-[1.6] text-nexus-text-secondary mb-8">
            Sign in to the console or ask your org admin for an invite.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/portal/login"
                  className="ig-cta h-12 px-6 rounded-xl text-[14px] font-medium inline-flex items-center gap-2"
                  style={{ background: '#1E4E8C', color: '#E6EEF9', boxShadow: 'var(--tinted-shadow-cta)' }}>
              Launch the console <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t py-8" style={{ borderColor: 'var(--hairline)' }}>
        <div className="max-w-[1280px] mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2.5" style={{ color: 'var(--nexus-text-tertiary)' }}>
            <CognaraLogo variant="icon" size={20} />
            <span className="ig-mono text-[11px]">© {new Date().getFullYear()} {BRAND.name} — {BRAND.tagline}</span>
          </div>
          <div className="flex items-center gap-5 text-[12px] text-nexus-text-tertiary">
            <a href="#" className="hover:text-nexus-text-primary transition-colors">Security</a>
            <a href="#" className="hover:text-nexus-text-primary transition-colors">Privacy</a>
            <a href="#" className="hover:text-nexus-text-primary transition-colors">Status</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ---------- helpers ---------- */

const BentoTile = ({ col, icon: Icon, eyebrow, title, body, demo }) => (
  <StaggerItem className={`${col}`}>
    <div className="group relative h-full p-6 md:p-8 rounded-2xl transition-all duration-200 overflow-hidden"
         style={{
           background: 'var(--layer-2)',
           border: '1px solid var(--hairline)',
           boxShadow: 'var(--inner-glow)',
         }}>
      <div className="flex items-start justify-between mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
             style={{ background: 'rgba(30,78,140,0.10)', border: '1px solid rgba(30,78,140,0.2)' }}>
          <Icon size={18} className="text-nexus-accent-blue" strokeWidth={1.75} />
        </div>
        <ArrowUpRight size={14} className="text-nexus-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h3 className="ig-display text-[22px] mt-2 mb-2.5 text-nexus-text-primary tracking-[-0.015em]">{title}</h3>
      <p className="text-[14px] leading-[1.6] text-nexus-text-secondary">{body}</p>
      {demo && <div className="mt-5">{demo}</div>}
    </div>
  </StaggerItem>
);

const HeroVignette = () => (
  <div className="relative mx-auto max-w-[520px]">
    <div className="relative rounded-2xl overflow-hidden"
         style={{
           background: 'var(--layer-2)',
           border: '1px solid rgba(30,78,140,0.4)',
           boxShadow: 'var(--inner-glow), 0 30px 80px -20px rgba(30,78,140,0.4)',
         }}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--hairline)' }}>
        <span className="ig-live-dot" />
        <span className="ig-eyebrow text-[9px]" style={{ color: 'var(--nexus-accent-blue)' }}>Cognara Engine · live</span>
        <div className="flex-1" />
        <span className="ig-mono text-[10px] text-nexus-text-tertiary">Ultra 4.7</span>
      </div>
      {/* Body mock */}
      <div className="p-5 space-y-4">
        <div className="flex justify-end">
          <div className="max-w-[85%] text-[12px] rounded-xl px-3 py-2" style={{ background: 'rgba(30,78,140,0.25)', color: '#E6EEF9' }}>
            Summarize cost variance across WBS 2.3 and draft the RFI.
          </div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'var(--layer-3)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}>
            <CognaraLogo variant="icon" size={14} />
          </div>
          <div className="flex-1 text-[12px] leading-[1.6] text-nexus-text-secondary">
            <TypingLine />
            <div className="mt-3 rounded-lg p-3" style={{ background: 'var(--layer-3)', border: '1px solid var(--hairline)' }}>
              <div className="flex items-center justify-between text-[10px] mb-2">
                <span className="ig-eyebrow" style={{ color: 'var(--nexus-text-tertiary)' }}>CPI · WBS 2.3</span>
                <PulsingValue />
              </div>
              <div className="grid grid-cols-7 items-end gap-1 h-12">
                {[30, 45, 52, 48, 61, 58, 72].map((h, i) => (
                  <div key={i} className="rounded-sm" style={{ height: `${h}%`, background: i === 6 ? '#F26B1F' : '#1E4E8C', opacity: i === 6 ? 1 : 0.6 }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Footer chip */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-t" style={{ borderColor: 'var(--hairline)' }}>
        <FileText size={11} className="text-nexus-text-tertiary" />
        <span className="ig-mono text-[10px] text-nexus-text-tertiary">3 sources · auto-cited</span>
      </div>
    </div>
  </div>
);

const TypingLine = () => (
  <span>
    Variance is concentrated in procurement — Rev-3 quotes on HX-102 came in 8.4% above estimate.
    <motion.span
      animate={{ opacity: [0, 1, 0] }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      className="inline-block ml-0.5 w-[6px] h-[11px] align-middle"
      style={{ background: 'var(--nexus-accent-blue)' }}
    />
  </span>
);

const PulsingValue = () => {
  return (
    <motion.span
      className="ig-mono text-[11px]"
      animate={{ color: ['#F26B1F', '#E4E7ED', '#F26B1F'] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
    >
      0.918
    </motion.span>
  );
};

const MemoryDemo = () => (
  <div className="mt-2 rounded-xl p-4"
       style={{ background: 'var(--layer-3)', border: '1px solid var(--hairline)' }}>
    <div className="flex items-center gap-2 mb-3">
      <span className="ig-live-dot" />
      <span className="ig-eyebrow" style={{ color: 'var(--nexus-accent-blue)' }}>live context</span>
      <div className="flex-1" />
      <span className="ig-mono text-[10px] text-nexus-text-tertiary">75 compressed · 3 pinned</span>
    </div>
    <div className="space-y-1.5">
      {[
        'Budget ceiling: ₹82 crore INR',
        'FEED start: 2026-05-15',
        'Never include customer PII in deliverables',
      ].map((f, i) => (
        <div key={i} className="flex items-center gap-2 text-[12px] text-nexus-text-secondary">
          <span className="inline-block w-1 h-1 rounded-full flex-shrink-0" style={{ background: '#F26B1F' }} />
          {f}
        </div>
      ))}
    </div>
  </div>
);

const TelemetryDemo = () => (
  <div className="mt-2 rounded-xl p-4 h-[160px] flex items-end gap-1.5"
       style={{ background: 'var(--layer-3)', border: '1px solid var(--hairline)' }}>
    {[30, 45, 28, 62, 51, 78, 44, 68, 82, 55, 71, 89].map((h, i) => (
      <motion.div
        key={i}
        initial={{ height: 0 }}
        whileInView={{ height: `${h}%` }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
        className="flex-1 rounded-sm"
        style={{ background: i === 11 ? '#F26B1F' : '#1E4E8C', opacity: i === 11 ? 1 : 0.55 }}
      />
    ))}
  </div>
);

export default Landing;
